import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { SouthEastOutlined, EastOutlined, CalendarTodayOutlined } from '@mui/icons-material';
import FuseDialog from '@fuse/core/FuseDialog';
import { closeDialog } from 'app/store/fuse/dialogSlice';
import DIALOGS from 'app/constants/dialogs';
import { TRANSLATION_KEY } from '../constants';

function MinusIcon() {
  return (
    <Box component="svg" viewBox="0 0 20 20" fill="none" sx={{ width: 20, height: 20, flexShrink: 0 }}>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
          d="M2.91675 10C2.91675 9.30964 3.47639 8.75 4.16675 8.75H15.8334C16.5238 8.75 17.0834 9.30964 17.0834 10C17.0834 10.6904 16.5238 11.25 15.8334 11.25H4.16675C3.47639 11.25 2.91675 10.6904 2.91675 10Z"
          fill="#E94029"
        />
      </svg>
    </Box>
  );
}

const DowngradeConfirmDialog = ({ currentPlan, newPlan, renewsOn, isLoading, onConfirm }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_KEY);
  const [agreed, setAgreed] = useState(false);

  const onClose = useCallback(() => {
    dispatch(closeDialog());
    setTimeout(() => setAgreed(false), 300);
  }, [dispatch]);

  const handleConfirm = () => {
    if (!agreed || isLoading) return;
    onConfirm?.(newPlan?.tariffPlanId, newPlan?.tariffPlanVariantId);
  };

  return (
    <FuseDialog
      fullWidth
      maxWidth="sm"
      dialogId={DIALOGS.DOWNGRADE_CONFIRM}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: '701px', borderRadius: '16px' } }}
    >
      <Box padding="32px" display="flex" flexDirection="column" gap="24px">
        {/* ── Header ── */}
        <Box display="flex" flexDirection="column" gap="12px">
          <Box display="flex" alignItems="center" gap="10px">
            {/* Downgrade icon — red circle with southeast arrow */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SouthEastOutlined sx={{ fontSize: '13px', color: 'white' }} />
            </Box>

            <Typography variant="h5" fontWeight={700} color="accent.900" lineHeight="32px" fontSize="23px">
              {t('DOWNGRADE_PLAN')}
            </Typography>
          </Box>
        </Box>

        {/* ── Plan comparison card ── */}
        <Box
          bgcolor="white"
          border="1px solid"
          borderColor="grey.100"
          borderRadius="12px"
          px="20px"
          py="16px"
          display="flex"
          flexDirection="column"
          gap="16px"
        >
          {/* Current → New */}
          <Box display="flex" alignItems="center" gap="24px" px="4px">
            {/* Current plan — shown in violet (it's the higher plan) */}
            <Box flex={1} display="flex" flexDirection="column" gap="6px">
              <Typography variant="body1" fontWeight={500} color="accent.900" fontSize="16px">
                {t('CURRENT_PLAN_LABEL')}:{' '}
                <Box component="span" fontWeight={700} color="secondary.main">
                  {currentPlan?.name}
                </Box>
              </Typography>
              <Typography variant="body2" color="accent.900" fontSize="14px" fontWeight={400}>
                <Box component="span" fontWeight={700}>
                  {currentPlan?.price}
                </Box>{' '}
                {currentPlan?.period}
              </Typography>
            </Box>

            {/* Arrow */}
            <EastOutlined sx={{ color: 'accent.700', flexShrink: 0, fontSize: '20px' }} />

            {/* New plan — plain color (it's the lower plan) */}
            <Box flex={1} display="flex" flexDirection="column" gap="6px">
              <Typography variant="body1" fontWeight={500} color="accent.900" fontSize="16px">
                {t('NEW_PLAN_LABEL')}:{' '}
                <Box component="span" fontWeight={700}>
                  {newPlan?.name}
                </Box>
              </Typography>
              <Typography variant="body2" color="accent.900" fontSize="14px" fontWeight={400}>
                <Box component="span" fontWeight={700}>
                  {newPlan?.price}
                </Box>{' '}
                {newPlan?.period}
              </Typography>
            </Box>
          </Box>

          {/* Renewal info bar */}
          {renewsOn && (
            <Box
              bgcolor="grey.50"
              border="1px solid"
              borderColor="grey.200"
              borderRadius="8px"
              px="12px"
              py="8px"
              display="flex"
              alignItems="center"
              gap="6px"
              overflow="hidden"
            >
              <CalendarTodayOutlined sx={{ fontSize: '16px', color: 'secondary.main', flexShrink: 0 }} />
              <Typography
                variant="caption"
                fontWeight={500}
                color="accent.600"
                fontSize="12px"
                lineHeight="16px"
                noWrap
              >
                {renewsOn}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Changes section ── */}
        <Box display="flex" flexDirection="column" gap="6px">
          <Typography variant="body1" fontWeight={500} color="accent.900" fontSize="16px">
            {t('CHANGES')}:
          </Typography>
          <Box
            borderBottom="1px solid"
            borderColor="grey.200"
            display="flex"
            flexDirection="column"
            gap="4px"
            pb="16px"
          >
            {['DOWNGRADE_FEATURE_LIMITS', 'DOWNGRADE_FEATURE_ACCESS'].map((key) => (
              <Box key={key} display="flex" alignItems="center" gap="12px">
                <MinusIcon />
                <Typography
                  variant="body2"
                  color="accent.900"
                  fontSize="13px"
                  lineHeight="18px"
                  fontWeight={400}
                >
                  {t(key)}
                </Typography>
              </Box>
            ))}

            {/* Notes */}
            <Box mt="8px">
              <Typography
                variant="body2"
                color="accent.500"
                fontSize="13px"
                lineHeight="18px"
                fontWeight={400}
              >
                {t('DOWNGRADE_BILLING_NOTE')}
                <br />
                {t('DOWNGRADE_ACTIVE_NOTE')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Confirmation checkbox ── */}
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              size="small"
              sx={{
                padding: '1px',
                color: 'grey.400',
                '&.Mui-checked': { color: 'accent.800' },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              color="accent.900"
              fontSize="13px"
              lineHeight="18px"
              fontWeight={400}
              sx={{ ml: '4px' }}
            >
              {t('DOWNGRADE_UNDERSTAND_LABEL')}
            </Typography>
          }
          sx={{ alignItems: 'flex-start', margin: 0 }}
        />

        {/* ── Action buttons ── */}
        <Box display="flex" gap="12px" alignItems="center">
          <Button
            variant="contained"
            color="secondary"
            disabled={!agreed || isLoading}
            onClick={handleConfirm}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: '16px',
              py: '10px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {t('CONFIRM_DOWNGRADE')}
          </Button>
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              color: 'accent.900',
              borderRadius: '20px',
              px: '16px',
              py: '10px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {t('CANCEL')}
          </Button>
        </Box>
      </Box>
    </FuseDialog>
  );
};

export default DowngradeConfirmDialog;
